import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from '@/config-global';
import { supabase } from '@/lib/supabase';
import { readCurrentOrganizationId } from '@/utils/organization-selection';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

axiosInstance.interceptors.request.use(
  async (config) => {
    const currentOrganizationId = readCurrentOrganizationId();
    if (currentOrganizationId && !config.headers.has('X-Organization-Id')) {
      config.headers.set('X-Organization-Id', currentOrganizationId);
    }

    const providedAuth = (config.headers as any)?.Authorization || (config.headers as any)?.authorization;
    if (!providedAuth) {
      try {
        // First try to get the current session
        const {
          data: { session: initialSession },
          error: sessionError,
        } = await supabase.auth.getSession();
        
        let session = initialSession;
        
        // If no session, try to refresh it (might be stale)
        if (!session && !sessionError) {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshData?.session) {
            session = refreshData.session;
          }
          if (refreshError) {
            console.error('[axios] Failed to refresh session:', refreshError);
          }
        }
        
        if (sessionError) {
          console.error('[axios] Failed to get session:', sessionError);
        }
        
        if (session?.access_token) {
          (config.headers as any).Authorization = `Bearer ${session.access_token}`;
        } else {
          console.warn('[axios] No session found for request:', config.url);
        }
      } catch (error) {
        console.error('[axios] Error getting session:', error);
      }
    }
    if ((config.headers as any).authorization && !(config.headers as any).Authorization) {
      (config.headers as any).Authorization = (config.headers as any).authorization;
      delete (config.headers as any).authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong!')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  user: '/api/user',
  venues: '/api/venues',
  sidebar: '/api/sidebar',
  company: '/api/company',
  ai_chat: '/api/ai/chat',
  register: '/api/register',
  settings: '/api/settings',
  reporting: '/api/reporting',
  reporting_chat: '/api/reporting/chat',
  transactions: '/api/transactions',
  organization: '/api/organization',
};
